// Route Code Edited 4/30/26
/**
 * Route compatibility helper
 * 
 * This script improves the injected Route modal accessibility and automatically detects when:
 * 1. A page is restored from bfcache (pageshow event)
 * 2. Browser navigation occurs via back/forward buttons (popstate event)
 * 
 * Actions taken:
 * 1. Adds keyboard and focus handling for the Route package protection modal
 * 2. Checks for open cart drawers or if user is on cart page
 * 3. Removes Route items from cart if present
 * 4. Refreshes the page to update cart UI and reinitialize Route scripts
 * 
 */
(function() {
    const DRAWER_CART_SELECTOR = 'cart-drawer[open]';
    const ROUTE_MODAL_SELECTOR = '.route-modal.rm-open';
    const ROUTE_CLOSE_SELECTOR = '[data-testid="rm-close-button"], .route-modal__close';
    const FOCUSABLE_SELECTOR = [
        'a[href]',
        'area[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'iframe',
        'object',
        'embed',
        '[contenteditable]',
        '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    let activeRouteModal = null;
    let routeModalTrigger = null;
    let routeModalRaf = null;
    let routeModalEscapeHandled = false;
    let lastExternalFocus = null;

    // An element is only worth restoring focus to if it can actually take
    // focus and is still connected. Excludes body/documentElement so focus
    // restoration never resolves to a useless target.
    const isRestorable = (element) => {
        return !!(
            element &&
            element !== document.body &&
            element !== document.documentElement &&
            typeof element.focus === 'function' &&
            document.contains(element)
        );
    };

    const isVisible = (element) => {
        return !!(
            element &&
            element.getAttribute('aria-hidden') !== 'true' &&
            (element.offsetWidth || element.offsetHeight || element.getClientRects().length)
        );
    };

    const getRouteModal = () => {
        return document.querySelector(ROUTE_MODAL_SELECTOR);
    };

    const getFocusableElements = (container) => {
        return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
    };

    const getRouteCloseButton = (modal) => {
        return modal.querySelector(ROUTE_CLOSE_SELECTOR);
    };

    const getRouteModalHeading = (modal) => {
        return modal.querySelector('#rm-modal-title, .route-modal__title, [role="heading"]');
    };

    const focusRouteModal = (modal) => {
        // VoiceOver announces a programmatically-focused role="dialog" container
        // as a generic "group", so focus the title heading instead: screen
        // readers reliably announce its text and level ("We've got you covered,
        // heading level 2"), which tells the user what the dialog is. The close
        // button (earlier in the DOM) and links stay reachable via Tab/Shift+Tab.
        const heading = getRouteModalHeading(modal);
        const target = heading || modal;

        if (heading && !heading.hasAttribute('tabindex')) {
            heading.setAttribute('tabindex', '-1');
        }

        if (typeof target.focus === 'function') {
            target.focus({ preventScroll: true });
        }
    };

    const restoreRouteModalFocus = () => {
        const target = routeModalTrigger;

        activeRouteModal = null;
        routeModalTrigger = null;

        if (target && document.contains(target) && typeof target.focus === 'function') {
            target.focus({ preventScroll: true });
        }
    };

    const closeRouteModal = (modal) => {
        const closeButton = getRouteCloseButton(modal);

        if (closeButton) {
            closeButton.click();
        }
    };

    const enhanceRouteCloseButton = (modal) => {
        const closeButton = getRouteCloseButton(modal);

        if (!closeButton || closeButton.dataset.routeA11yEnhanced === 'true') return;

        if (closeButton.tagName !== 'BUTTON') {
            closeButton.setAttribute('role', 'button');
            closeButton.setAttribute('tabindex', '0');
        }

        closeButton.setAttribute('aria-label', 'Close package protection dialog');
        closeButton.dataset.routeA11yEnhanced = 'true';

        closeButton.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;

            event.preventDefault();
            closeButton.click();
        });
    };

    const trapRouteModalFocus = (event, modal) => {
        if (event.key !== 'Tab') return;

        const focusable = getFocusableElements(modal);

        if (!focusable.length) {
            event.preventDefault();
            event.stopPropagation();
            modal.focus({ preventScroll: true });
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && (event.target === first || event.target === modal)) {
            event.preventDefault();
            event.stopPropagation();
            last.focus({ preventScroll: true });
        }
        else if (!event.shiftKey && event.target === last) {
            event.preventDefault();
            event.stopPropagation();
            first.focus({ preventScroll: true });
        }
    };

    const handleRouteModalKeydown = (event) => {
        // This listener is global; bail before touching the DOM for the vast
        // majority of keystrokes (typing in search/forms). Only Tab and Escape
        // are relevant to the modal.
        if (event.key !== 'Tab' && event.key !== 'Escape') return;

        const modal = getRouteModal();
        if (!modal) return;

        if (event.key === 'Escape') {
            routeModalEscapeHandled = true;
            event.preventDefault();
            event.stopPropagation();
            closeRouteModal(modal);
            return;
        }

        trapRouteModalFocus(event, modal);
    };

    const handleRouteModalKeyup = (event) => {
        if (event.key !== 'Escape' && event.code !== 'Escape') return;
        if (!routeModalEscapeHandled) return;

        routeModalEscapeHandled = false;
        event.preventDefault();
        event.stopPropagation();
    };

    const handleRouteModalFocusin = (event) => {
        const modal = getRouteModal();

        // No modal open: remember the last restorable element focused outside
        // any modal, so focus returns to the real opener on close even if
        // Route moves focus before our observer runs.
        if (!modal) {
            if (isRestorable(event.target)) {
                lastExternalFocus = event.target;
            }
            return;
        }

        // Focus parked on the full-viewport container (our fallback, or Route
        // re-focusing it after us) — move it to the heading so VoiceOver's
        // cursor highlights the title, not the entire overlay.
        if (event.target === modal) {
            const heading = getRouteModalHeading(modal);
            if (heading && heading !== modal) {
                focusRouteModal(modal);
            }
            return;
        }

        if (modal.contains(event.target)) return;

        focusRouteModal(modal);
    };

    const syncRouteModalAccessibility = () => {
        routeModalRaf = null;

        const modal = getRouteModal();

        if (!modal) {
            if (activeRouteModal) {
                restoreRouteModalFocus();
            }
            return;
        }

        if (modal !== activeRouteModal) {
            // Prefer the element focused just before the modal appeared; fall
            // back to current focus only if it is itself restorable.
            const candidate = isRestorable(lastExternalFocus)
                ? lastExternalFocus
                : document.activeElement;

            routeModalTrigger = isRestorable(candidate) && !modal.contains(candidate)
                ? candidate
                : routeModalTrigger;
            activeRouteModal = modal;
        }

        if (!modal.id) {
            modal.id = 'rm-modal-container';
        }

        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('tabindex', '-1');

        // Self-heal the accessible name: a repaired role="dialog" with no
        // name still fails axe. Link the title if Route's markup includes it.
        if (!modal.getAttribute('aria-labelledby') && modal.querySelector('#rm-modal-title')) {
            modal.setAttribute('aria-labelledby', 'rm-modal-title');
        }

        enhanceRouteCloseButton(modal);

        // Bring focus to the heading when it's outside the modal, or "upgrade"
        // it from the full-viewport container (our fallback landing spot, set
        // before Route had rendered the heading) to the heading once available.
        const active = document.activeElement;
        if (!modal.contains(active) || (active === modal && getRouteModalHeading(modal))) {
            focusRouteModal(modal);
        }
    };

    const scheduleRouteModalAccessibilitySync = () => {
        if (routeModalRaf) return;

        routeModalRaf = requestAnimationFrame(syncRouteModalAccessibility);
    };
       
    const run = () => {
        const hasOpenDrawer = !!document.querySelector(DRAWER_CART_SELECTOR);
        const isCartPage = /\/cart\/?$/.test(location.pathname);
        
        // Only run on cart page or when drawer cart is open
        if (!hasOpenDrawer && !isCartPage) return;
        
        fetch('/cart.js')
            .then(res => res.ok ? res.json() : Promise.reject(res.status))
            .then(cart => {
                // Find Route item in cart
                const route = (cart.items || []).find(item => item.vendor === 'Route');
                
                // Remove Route item if present, otherwise resolve immediately
                if (route?.key) {
                    return fetch('/cart/update.js', {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({updates: {[route.key]: 0}})
                    });
                }
                return Promise.resolve(); // No Route item, continue to reload
            })
            .catch(error => {
                console.error('Cart operation failed:', error);
                // Continue to reload even if cart operations fail
            })
            .then(() => {
                location.reload(); // Refresh after removal completes (or if no Route item)
            });
    };
    
    // Handle bfcache restoration (forward/back navigation)
    addEventListener('pageshow', e => e.persisted && run());
    
    // Handle browser back button with delay for drawer state
    addEventListener('popstate', () => setTimeout(() => run(), 100));

    document.addEventListener('keydown', handleRouteModalKeydown, true);
    document.addEventListener('keyup', handleRouteModalKeyup, true);
    document.addEventListener('focusin', handleRouteModalFocusin);

    // The observer is document-wide, so ignore the constant class churn from
    // unrelated components. Only react when a Route modal element's class
    // changes (open/close toggles rm-open on .route-modal itself) or a node
    // that is / contains a Route modal is added or removed.
    const nodeListTouchesRouteModal = (nodes) => {
        for (const node of nodes) {
            if (node.nodeType !== 1) continue;
            if (node.classList.contains('route-modal')) return true;
            if (node.querySelector && node.querySelector('.route-modal')) return true;
        }
        return false;
    };

    const mutationTouchesRouteModal = (mutations) => {
        for (const mutation of mutations) {
            const target = mutation.target;

            // While a modal is already tracked, react to its internal content
            // being populated. Route inserts the container first and fills in
            // the heading/body a frame later, so without this we'd focus the
            // container (no heading yet) and never re-sync to upgrade to the
            // heading. activeRouteModal is null when no modal is open, so this
            // adds no cost in the common case.
            if (activeRouteModal && target.nodeType === 1 && activeRouteModal.contains(target)) {
                return true;
            }

            if (mutation.type === 'attributes') {
                if (target.nodeType === 1 && target.classList.contains('route-modal')) {
                    return true;
                }
                continue;
            }

            // Scan added and removed nodes independently: a replaceChild can
            // remove the Route modal while adding an unrelated node in the same
            // record, and the removal must still trigger a sync (focus restore).
            if (nodeListTouchesRouteModal(mutation.addedNodes)) return true;
            if (nodeListTouchesRouteModal(mutation.removedNodes)) return true;
        }
        return false;
    };

    new MutationObserver((mutations) => {
        if (mutationTouchesRouteModal(mutations)) {
            scheduleRouteModalAccessibilitySync();
        }
    }).observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true
    });

    scheduleRouteModalAccessibilitySync();
})();
// End Route Code
